/**
 * Decision replay (ARCHITECTURE.md §8, §0.3): the full audit of a single run —
 * its state/outcome/timing header plus the ordered, expandable decision trace
 * (indicators, condition trace, risk checks, AI verdict, order intent).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, formatDateTime } from "@richrich/ui";
import { useI18n } from "@richrich/i18n/react";
import { PageShell } from "../../components/PageShell";
import { runDecisionsQuery } from "../../features/strategies/api";
import { DecisionTrace } from "../../features/strategies/DecisionTrace";
import { OutcomeBadge, RunStateBadge, routeLabel } from "../../features/strategies/meta";

export const Route = createFileRoute("/strategies/$id/runs/$runId")({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(runDecisionsQuery(params.id, params.runId));
  },
  component: DecisionReplayPage,
});

function DecisionReplayPage() {
  const { t, locale } = useI18n();
  const { id, runId } = Route.useParams();
  const query = useQuery(runDecisionsQuery(id, runId));

  if (query.isPending) {
    return (
      <PageShell
        eyebrow={t("strategies.runEyebrowSingle")}
        title={t("strategies.decisionReplay")}
        loading
      />
    );
  }

  if (query.isError || query.data === undefined) {
    return (
      <PageShell eyebrow={t("strategies.runEyebrowSingle")} title={t("strategies.notFound")}>
        <div className="rounded-xl border border-dashed border-hairline py-14 text-center">
          <p className="text-sm font-medium text-ink">{t("strategies.runLoadError")}</p>
          <Link
            to="/strategies/$id"
            params={{ id }}
            className="mt-2 inline-block text-sm text-brand hover:underline"
          >
            {t("strategies.backToStrategy")}
          </Link>
        </div>
      </PageShell>
    );
  }

  const { run, decisions } = query.data;

  return (
    <PageShell
      eyebrow={t("strategies.runEyebrow", { tick: run.tickId.slice(0, 12) })}
      title={t("strategies.decisionReplay")}
      description={t("strategies.decisionReplayDescription")}
      actions={
        <Link
          to="/strategies/$id"
          params={{ id }}
          className="rounded-md border border-hairline px-3 py-1.5 text-sm font-medium text-ink-secondary transition-colors hover:bg-elevated"
        >
          {t("strategies.strategyAction")}
        </Link>
      }
    >
      <div className="flex flex-col gap-6">
        <Card title={t("strategies.runSummary")}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-1.5">
              <RunStateBadge state={run.state} />
              <OutcomeBadge outcome={run.outcome} />
              {run.dryRun && (
                <span className="font-mono text-[10px] uppercase tracking-wide text-muted">
                  dry-run
                </span>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-4">
              <Meta label={t("strategies.metaRoute")} value={routeLabel(run.route, t)} />
              <Meta
                label={t("strategies.metaScheduled")}
                value={formatDateTime(run.scheduledAt, locale)}
              />
              <Meta
                label={t("strategies.metaStarted")}
                value={run.startedAt !== null ? formatDateTime(run.startedAt, locale) : "—"}
              />
              <Meta
                label={t("strategies.metaFinished")}
                value={run.finishedAt !== null ? formatDateTime(run.finishedAt, locale) : "—"}
              />
            </dl>
            {run.error !== null && (
              <p className="rounded-md border border-critical/30 bg-critical/5 px-3 py-2 font-mono text-xs text-critical">
                {run.error}
              </p>
            )}
          </div>
        </Card>

        <DecisionTrace decisions={decisions} />
      </div>
    </PageShell>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-muted">{label}</dt>
      <dd className="mt-0.5 truncate font-mono text-sm text-ink" title={value}>
        {value}
      </dd>
    </div>
  );
}
